import { useCallback, useEffect, useRef, useState } from "react";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useChapterMemoPolling } from "./useChapterMemoPolling";

type TGenerationStatus =
  | "pending"
  | "processing"
  | "streaming"
  | "ready"
  | "processed"
  | "error"
  | "errorSeen"
  | "cancelled";

export type TSnippetStatus = {
  slug: string;
  status: "done" | "pending" | "error" | "processing";
};

export type TChapterPhase = "documentParsing" | "chapterGeneration" | "chapterWaiting" | "chapterConfiguration";

export type TChapterStatusEntry = {
  phase: TChapterPhase;
  message: string;
  snippetsStatus: TSnippetStatus[];
};

export type TChapterStatusMap = Record<string, TChapterStatusEntry>;

export type TSetupPhase = "orienting" | "modeling" | "ledger" | "chapters" | "twin" | "storing";

export type TSetupProgress = {
  startedAt: string;
  etaSeconds: number;
  phase: TSetupPhase;
  lastAction: string | null;
};

type TProjectSetupStatus = {
  status: TGenerationStatus;
  id: number;
  result?: string;
  statusMessage: string;
  setup?: TSetupProgress | null;
};

type TActiveTaskResponse = {
  active: boolean;
  generationId?: number;
  type?: string;
  failedGenerationId?: number;
  failedError?: string;
};

export type TCancelSetupResult = {
  projectId: number;
  caseBriefId: number;
  isRelaunchBrief: boolean;
};

const GENERIC_SETUP_ERROR = "Project setup failed";

function taskErrorMessage(result?: string): string {
  try {
    const parsed = result ? JSON.parse(result) : null;
    return parsed && typeof parsed.error === "string" ? parsed.error : GENERIC_SETUP_ERROR;
  } catch {
    return GENERIC_SETUP_ERROR;
  }
}

function parseChapterStatusMap(statusMessage: string): TChapterStatusMap | null {
  if (!statusMessage) return null;
  try {
    const parsed = JSON.parse(statusMessage);
    if (typeof parsed === "object" && parsed !== null) {
      const result: TChapterStatusMap = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (value && typeof value === "object" && "message" in value) {
          result[key] = value as TChapterStatusEntry;
        }
      }
      return Object.keys(result).length > 0 ? result : null;
    }
    return null;
  } catch {
    return null;
  }
}

function getProgressMessageFromMap(
  chapterStatusMap: TChapterStatusMap | null,
  chapterId: number | null
): string | null {
  if (!chapterStatusMap || !chapterId) return null;
  const chapterIdStr = chapterId.toString();
  const entry = chapterStatusMap[chapterIdStr];
  return entry?.message ?? null;
}

const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export function useProjectSetupPolling(
  projectId: string,
  activeChapterId: number | null,
  enabled = true,
  onComplete?: () => void
) {
  const {
    setSetupTask,
    clearSetupTask,
    updateTaskStatus,
    setPolling,
    setChapterStatusMap: setStoredChapterStatusMap,
    setSetupProgress: setStoredSetupProgress,
    setSetupFailure,
    clearSetupFailure
  } = useProjectSetupStore();

  const taskId = useProjectSetupStore((state) => state.activeSetupTasks[projectId] || null);
  const storedStatus = useProjectSetupStore((state) => state.taskStatuses[projectId] || null);

  const [isPolling, setIsPolling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPollingInitialized, setIsPollingInitialized] = useState(false);
  const [status, setStatus] = useState<TGenerationStatus | null>(storedStatus);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const [progressMessage, setProgressMessage] = useState("Setting up project...");
  const [chapterStatusMap, setChapterStatusMap] = useState<TChapterStatusMap | null>(null);
  const [setup, setSetup] = useState<TSetupProgress | null>(null);
  const hasCheckedForActiveTask = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const chapterMemoPolling = useChapterMemoPolling(projectId, isProcessing, 3000);

  useEffect(() => {
    hasCheckedForActiveTask.current = false;
  }, [projectId]);

  useEffect(() => {
    if (storedStatus) {
      setStatus(storedStatus);
    }
  }, [storedStatus]);

  useEffect(() => {
    const message = getProgressMessageFromMap(chapterStatusMap, activeChapterId);
    if (message) {
      setProgressMessage(message);
    }
  }, [activeChapterId, chapterStatusMap]);

  const stopPolling = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
    setPolling(projectId, false);
  };

  const checkForActiveTask = useCallback(async () => {
    if (!projectId || !enabled || hasCheckedForActiveTask.current) {
      return;
    }

    hasCheckedForActiveTask.current = true;

    try {
      const response = await $api.get<TActiveTaskResponse>("/chat/check-active", {
        params: { projectId, type: "projectSetup" }
      });

      if (response.data.active && response.data.generationId) {
        clearSetupFailure(projectId);
        setSetupTask(projectId, response.data.generationId);
        setPolling(projectId, true);
      } else {
        if (response.data.failedError) {
          setSetupFailure(projectId, response.data.failedError);
        }
        const hasPendingTaskInStore = !!useProjectSetupStore.getState().activeSetupTasks[projectId];
        if (!hasPendingTaskInStore) {
          setIsPolling(false);
          setPolling(projectId, false);
        }
        setIsPollingInitialized(true);
      }
    } catch (error) {
      console.error("Error checking for active setup task:", error);
      hasCheckedForActiveTask.current = false;
      checkRetryTimeoutRef.current = setTimeout(() => {
        checkForActiveTask();
      }, 7000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, enabled, setSetupTask, setPolling, setSetupFailure, clearSetupFailure]);

  const poll = async () => {
    if (!taskId || !enabled) {
      stopPolling();
      return;
    }

    try {
      const response = await $api.get<TProjectSetupStatus>(`/generation-tasks/${taskId}/status`);
      setIsPollingInitialized(true);
      consecutiveErrorsRef.current = 0;

      const taskStatus = response.data.status;
      const statusMessage = response.data.statusMessage;

      setStatus(taskStatus);
      updateTaskStatus(projectId, taskStatus);

      const parsedStatusMap = parseChapterStatusMap(statusMessage);
      setChapterStatusMap(parsedStatusMap);
      setStoredChapterStatusMap(projectId, parsedStatusMap);
      setSetup(response.data.setup ?? null);
      setStoredSetupProgress(projectId, response.data.setup ?? null);
      const dynamicMessage = getProgressMessageFromMap(parsedStatusMap, activeChapterId);

      switch (taskStatus) {
        case "pending":
          setProgressMessage(dynamicMessage ?? "");
          setIsPolling(true);
          pollingTimeoutRef.current = setTimeout(poll, 7000);
          break;

        case "processing":
          setProgressMessage(dynamicMessage ?? "");
          setIsProcessing(true);
          setIsPolling(true);
          pollingTimeoutRef.current = setTimeout(poll, 7000);
          break;

        case "processed":
        case "ready":
          setProgressMessage("Project setup complete!");
          setIsProcessing(false);
          clearSetupFailure(projectId);
          clearSetupTask(projectId);
          stopPolling();
          if (onComplete) {
            onComplete();
          }
          break;

        case "error":
        case "errorSeen": {
          setProgressMessage(GENERIC_SETUP_ERROR);
          setIsProcessing(false);
          setSetupFailure(projectId, taskErrorMessage(response.data.result));
          clearSetupTask(projectId);
          stopPolling();
          break;
        }

        case "cancelled":
          setProgressMessage("Project setup cancelled");
          setIsProcessing(false);
          clearSetupTask(projectId);
          stopPolling();
          break;

        default:
          setIsProcessing(false);
          stopPolling();
      }
    } catch (error) {
      console.error("Error polling project setup status:", error);
      consecutiveErrorsRef.current += 1;
      // A transient failure (backend redeploy, network blip) must not kill the loop for good —
      // a dead loop leaves generating chapters without loaders until a full page reload.
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
        stopPolling();
        return;
      }
      setIsPolling(true);
      pollingTimeoutRef.current = setTimeout(poll, 7000);
    }
  };

  const cancelSetup = async (): Promise<TCancelSetupResult | null> => {
    if (!taskId || isCancelling) return null;

    setIsCancelling(true);
    try {
      const res = await $api.post<{ data: TCancelSetupResult }>(`/projects/${projectId}/cancel-generation`);
      setStatus("cancelled");
      setProgressMessage("Project setup cancelled");
      setIsProcessing(false);
      clearSetupFailure(projectId);
      clearSetupTask(projectId);
      stopPolling();
      return res.data.data;
    } catch (error) {
      console.error("Error cancelling project setup:", error);
      toast.error("Failed to cancel generation");
      return null;
    } finally {
      setIsCancelling(false);
    }
  };

  const retrySetup = async (): Promise<boolean> => {
    if (isRetrying) return false;

    setIsRetrying(true);
    try {
      const res = await $api.post<{ taskId: number }>(`/projects/${projectId}/auto-setup`, { prefill: true });
      clearSetupFailure(projectId);
      setStatus("pending");
      setProgressMessage("Setting up project...");
      hasCheckedForActiveTask.current = true;
      setSetupTask(projectId, res.data.taskId);
      setPolling(projectId, true);
      return true;
    } catch (error) {
      console.error("Error retrying project setup:", error);
      toast.error("Failed to retry analysis");
      return false;
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    checkForActiveTask();
    return () => {
      if (checkRetryTimeoutRef.current) {
        clearTimeout(checkRetryTimeoutRef.current);
        checkRetryTimeoutRef.current = null;
      }
    };
  }, [checkForActiveTask]);

  useEffect(() => {
    if (enabled && taskId && !isPolling) {
      poll();
    }

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, enabled]);

  return {
    isPolling,
    status,
    progressMessage,
    chapterStatusMap,
    setup,
    stopPolling,
    chapterMemoPolling,
    isPollingInitialized,
    cancelSetup,
    isCancelling,
    canCancel: !!taskId,
    retrySetup,
    isRetrying
  };
}
